import {
    CardCollection,
    DateIO,
    Instance,
    Knot,
    Objekt,
    Promize,
    Query,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { AssetService } from '@services/assetService';
import { DiscoveryService } from '@services/discoveryService';
import { LanguageService } from '@services/languageService';
import { ObservationService } from '@services/observationService';
import { OrganizationService } from '@services/organizationService';
import { UtilService } from '@services/utilService';
import { initCardCollection } from '@utils/collectionSetup';
import { DashboardController } from './dashboardController';

export class DiscoveriesIndexController extends DashboardController {
    static readonly inject = [
        'instances',
        'discoveryService',
        'observationService',
        'organizationService',
        'utilService',
        'assetService',
        'languageService',
    ] as const;

    discoveryCardCollection!: CardCollection;
    observationCardCollection!: CardCollection;

    constructor(
        instances: Instance,
        private discoveryService: DiscoveryService,
        private observationService: ObservationService,
        private organizationService: OrganizationService,
        private utilService: UtilService,
        private assetService: AssetService,
        private languageService: LanguageService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this._initToolbarButtons();
        this._initFilterForm();
        this._initTabPanel('discoveries');

        this._drawContent();
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton('.btn-site-home', this.dom, () => {
            this.state.goBack('site.home');
        });
    }

    protected override _drawContent(): Promize | boolean {
        this._initDiscoveryCardCollection();
        this._initObservationCardCollection();
        return true;
    }

    private _initDiscoveryCardCollection(): void {
        this.discoveryCardCollection = initCardCollection(
            this.discoveryCardCollection,
            this.dom,
            '.discovery-collection',
            this.discoveryService,
            {
                entityKey: 'discoveries',
                fetchData: (params) =>
                    this.discoveryService.getAll(params),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                    params.set(
                        'fields',
                        'id,full_name,photo,killing_at,person.name,quantity,' +
                            this.organizationService.getAssociationTableFields(),
                    );
                },
            },
            this._discoveryCardKnot.bind(this),
            { formatKillingAt: this.formatKillingAt.bind(this) },
            { sort: { column: 'killing_at', order: 'desc' } },
            'discoveries',
            this.activeTab,
        )!;
    }

    private _initObservationCardCollection(): void {
        this.observationCardCollection = initCardCollection(
            this.observationCardCollection,
            this.dom,
            '.observation-collection',
            this.observationService,
            {
                entityKey: 'observations',
                fetchData: (params) =>
                    this.observationService.getAll(params),
                extraParams: (params) => {
                    params.set('query', this.query);
                    params.set('organization_ids', this.organizationIds);
                    params.set(
                        'fields',
                        'id,short_description,photo,location.address,' +
                            this.organizationService.getAssociationTableFields(),
                    );
                },
            },
            this._observationCardKnot.bind(this),
            undefined,
            { sort: { column: 'name', order: 'desc' } },
            'observations',
            this.activeTab,
        )!;
    }

    private _observationCardKnot(cardKnot: Knot, observation: Objekt): void {
        this._initObservationShowButton(cardKnot, observation);
        this.utilService.initOrganizationLink(cardKnot, observation);
    }

    private _initObservationShowButton(
        cardKnot: Knot,
        observation: Objekt,
    ): void {
        this.helper.link(
            'a.card',
            cardKnot,
            (href) => {
                this.state.go(href);
            },
            this.state.resolveUrl('observations.show', {
                observationId: observation.get<string>('id'),
            }),
        );
    }

    formatKillingAt(killingAt: string): string {
        return this.languageService.format(
            DateIO.parse(killingAt),
            'format.datetime',
        );
    }

    private _discoveryCardKnot(cardKnot: Knot, discovery: Objekt): void {
        this._initDiscoveryContent(cardKnot, discovery);
        this._initDiscoveryShowButton(cardKnot, discovery);
        this.utilService.initOrganizationLink(cardKnot, discovery);
    }

    private _initDiscoveryContent(cardKnot: Knot, discovery: Objekt): void {
        const cardContent = new Query('.card .content', cardKnot).getKnot();
        const infoBoxKnot = new Knot('div');
        infoBoxKnot.addClass(['col-12', 'box', 'info']);
        const iconKnot = new Knot('em');
        iconKnot.addClass('material-icons');
        infoBoxKnot.appendChild(iconKnot);
        const textKnot = new Knot('span');
        infoBoxKnot.appendChild(textKnot);
        cardContent.appendChild(infoBoxKnot);

        const personName = discovery.get<string>('person.name', '');
        if (personName) {
            iconKnot.setHtml('person');
            textKnot.setHtml(personName);
        } else {
            iconKnot.setHtml('center_focus_strong');
            textKnot.setHtml(discovery.get<string>('quantity'));
        }
    }

    private _initDiscoveryShowButton(cardKnot: Knot, discovery: Objekt): void {
        this.helper.link(
            'a.card',
            cardKnot,
            (href) => {
                this.state.go(href);
            },
            this.state.resolveUrl('discoveries.show', {
                discoveryId: discovery.get<string>('id'),
            }),
        );
    }
}

export const discoveriesIndexController = app.controller(
    'discoveriesIndexController',
    DiscoveriesIndexController,
);
